/**
 * "Would you consider locking money away for a guaranteed income for life?" — the client's answer,
 * read by every module's candidate generator.
 *
 * Owner, 24-25 September 2026. The village Suggestion Engine asked this in its Strategy Analysis
 * Setup and no other module did, so a client who had said no in one place was still shown annuity
 * strategies everywhere else. The willingness is a property of the CLIENT, not of the module, so it
 * is saved once on the client record and honoured by all of them.
 *
 * Asked in everyday words, never as "% into an annuity" — a client should not need to know what an
 * annuity is to answer. The wording lives in the screens; this holds the answer and the rules.
 */
import type { FactFind } from './factFindTypes.ts';
import { guaranteedShapeAvailable } from './carePlusSplit.ts';

/** Whether an annuity may be offered at all. */
export type AnnuityAppetite = 'none' | 'yes' | 'unsure';
/**
 * Which shapes to build when the answer is yes. 'both' is the default and the unsure case.
 *
 * 'guaranteed' (27 Sep 2026) is the annuity-plus-life-policy shape: the full amount to the estate
 * whenever death comes. It is a different product from a capital-returned plain annuity, whose
 * death benefit tapers on the capital access schedule, and a client who asks for "100% back" means
 * this one. Until it existed here, that client was silently given the tapering shape instead.
 */
export type AnnuityResidual = 'returned' | 'nil' | 'guaranteed' | 'both';

export interface AnnuityPreference {
    appetite: AnnuityAppetite;
    residual: AnnuityResidual;
    /** Whether the client has actually answered, as opposed to falling back to the default. */
    answered: boolean;
}

const APPETITES: AnnuityAppetite[] = ['none', 'yes', 'unsure'];
const RESIDUALS: AnnuityResidual[] = ['returned', 'nil', 'guaranteed', 'both'];

/**
 * The saved answer, or the default: "not sure", which models both versions — the behaviour every
 * module had before the question existed, so an unanswered profile is never quietly narrowed.
 *
 * `goals.rvAnnuityAppetite` / `rvAnnuityResidual` are read as a fallback: the village asked first,
 * under its own field names, and a profile answered then must not lose its answer.
 */
export function annuityPreferenceOf(factFind: FactFind | null | undefined): AnnuityPreference {
    const goals = (factFind?.goals || {}) as Record<string, unknown>;
    const rawAppetite = goals.annuityAppetite ?? goals.rvAnnuityAppetite;
    const rawResidual = goals.annuityResidual ?? goals.rvAnnuityResidual;
    const appetite = APPETITES.includes(rawAppetite as AnnuityAppetite) ? (rawAppetite as AnnuityAppetite) : 'unsure';
    const residual = RESIDUALS.includes(rawResidual as AnnuityResidual) ? (rawResidual as AnnuityResidual) : 'both';
    return { appetite, residual, answered: APPETITES.includes(rawAppetite as AnnuityAppetite) };
}

/** Whether ANY annuity candidate may be generated. */
export function allowsAnnuity(preference: AnnuityPreference): boolean {
    return preference.appetite !== 'none';
}

/**
 * Whether a candidate with this residual share (0 to 1) may be generated.
 *
 * "Not sure" keeps both, whatever residual answer is stored: the second question is only asked after
 * a yes, so a stale answer behind it must not narrow the field.
 */
export function allowsResidual(preference: AnnuityPreference, residualCapital: number): boolean {
    if (!allowsAnnuity(preference)) return false;
    if (preference.appetite !== 'yes' || preference.residual === 'both') return true;
    // A client who asked for the full amount whenever they die has asked for the guaranteed shape,
    // not for either plain one; building a tapering annuity for them would be the old mistake.
    if (preference.residual === 'guaranteed') return false;
    const returnsCapital = Number(residualCapital) > 0;
    return preference.residual === 'returned' ? returnsCapital : !returnsCapital;
}

/**
 * Whether the GUARANTEED shape may be generated for this client.
 *
 * Two conditions, both necessary. The client's answer must allow it — a straight ask for it, or
 * "show me whichever works best", or no answer yet — and the client must be someone the product is
 * sold to: receiving or entering government-subsidised aged care (`guaranteedShapeAvailable`). The
 * second is a rule about the person, not the module, so it is read from the fact-find here rather
 * than decided by whichever screen is asking.
 */
export function allowsGuaranteed(preference: AnnuityPreference, factFind: FactFind | null | undefined, owner: 'client' | 'partner' | 'joint' = 'client'): boolean {
    if (!allowsAnnuity(preference)) return false;
    const wanted = preference.appetite !== 'yes' || preference.residual === 'guaranteed' || preference.residual === 'both';
    return wanted && guaranteedShapeAvailable(factFind, owner);
}

/** Convenience for a generator that holds the fact-find rather than a preference. */
export function annuityAllowedFor(factFind: FactFind | null | undefined, residualCapital: number): boolean {
    return allowsResidual(annuityPreferenceOf(factFind), residualCapital);
}
